import { useEffect, useState, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
    Loader2,
    Upload,
    Trash2,
    LogOut,
    ArrowLeft,
    ArrowUp,
    ArrowDown,
} from "lucide-react";
import { Link } from "react-router-dom";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "vd_admin_token";

export default function Admin() {
    const [token, setToken] = useState(() =>
        typeof window !== "undefined" ? localStorage.getItem(TOKEN_KEY) : null,
    );
    const [password, setPassword] = useState("");
    const [submitting, setSubmitting] = useState(false);
    const [images, setImages] = useState([]);
    const [uploading, setUploading] = useState(false);
    const [reordering, setReordering] = useState(false);
    const [caption, setCaption] = useState("");
    const fileRef = useRef(null);

    const headers = token ? { Authorization: `Bearer ${token}` } : {};

    const loadGallery = async () => {
        try {
            const res = await axios.get(`${API}/gallery`);
            setImages(Array.isArray(res.data) ? res.data : []);
        } catch (e) {
            // ignore
        }
    };

    useEffect(() => {
        if (!token) return;
        axios
            .get(`${API}/admin/verify`, { headers })
            .then(() => loadGallery())
            .catch(() => {
                localStorage.removeItem(TOKEN_KEY);
                setToken(null);
            });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [token]);

    const login = async (e) => {
        e.preventDefault();
        setSubmitting(true);
        try {
            const res = await axios.post(`${API}/admin/login`, { password });
            const t = res.data.token;
            localStorage.setItem(TOKEN_KEY, t);
            setToken(t);
            setPassword("");
            toast.success("Welcome back, Vickie.");
        } catch (err) {
            toast.error("Invalid password.");
        } finally {
            setSubmitting(false);
        }
    };

    const logout = async () => {
        try {
            await axios.post(`${API}/admin/logout`, {}, { headers });
        } catch (e) {
            // ignore
        }
        localStorage.removeItem(TOKEN_KEY);
        setToken(null);
        setImages([]);
        toast.success("Signed out.");
    };

    const onFile = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        setUploading(true);
        try {
            const fd = new FormData();
            fd.append("file", file);
            await axios.post(
                `${API}/admin/gallery/upload?caption=${encodeURIComponent(caption)}`,
                fd,
                {
                    headers: {
                        ...headers,
                        "Content-Type": "multipart/form-data",
                    },
                },
            );
            toast.success("Photo uploaded.");
            setCaption("");
            if (fileRef.current) fileRef.current.value = "";
            await loadGallery();
        } catch (err) {
            toast.error(
                err?.response?.data?.detail || "Upload failed. Try again.",
            );
        } finally {
            setUploading(false);
        }
    };

    const remove = async (id) => {
        if (!window.confirm("Delete this photo?")) return;
        try {
            await axios.delete(`${API}/admin/gallery/${id}`, { headers });
            toast.success("Deleted.");
            setImages((imgs) => imgs.filter((x) => x.id !== id));
        } catch (e) {
            toast.error("Delete failed.");
        }
    };

    const move = async (idx, direction) => {
        const targetIdx = idx + direction;
        if (targetIdx < 0 || targetIdx >= images.length) return;
        const next = [...images];
        [next[idx], next[targetIdx]] = [next[targetIdx], next[idx]];
        setImages(next);
        setReordering(true);
        try {
            await axios.post(
                `${API}/admin/gallery/reorder`,
                { order: next.map((i) => i.id) },
                { headers },
            );
        } catch (e) {
            toast.error("Reorder failed.");
            await loadGallery();
        } finally {
            setReordering(false);
        }
    };

    if (!token) {
        return (
            <main
                data-testid="admin-login-page"
                className="min-h-screen bg-[#F7F5F0] text-[#1A1A1A] flex items-center justify-center px-6"
            >
                <form
                    onSubmit={login}
                    data-testid="admin-login-form"
                    className="w-full max-w-md"
                >
                    <Link
                        to="/"
                        data-testid="admin-back-link"
                        className="inline-flex items-center gap-2 eyebrow mb-10"
                    >
                        <ArrowLeft size={14} /> Back to site
                    </Link>
                    <p className="eyebrow">Admin</p>
                    <h1 className="font-serif-display text-5xl md:text-6xl mt-3 mb-12 leading-none">
                        Sign <span className="italic">in.</span>
                    </h1>
                    <label className="block mb-8">
                        <span className="eyebrow block mb-2">Password</span>
                        <input
                            type="password"
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            data-testid="admin-password-input"
                            className="input-edit"
                            placeholder="••••••••"
                            autoFocus
                            required
                        />
                    </label>
                    <button
                        type="submit"
                        disabled={submitting}
                        data-testid="admin-login-submit"
                        className="btn-ink w-full disabled:opacity-50"
                    >
                        {submitting ? (
                            <>
                                <Loader2 size={14} className="animate-spin" />
                                Signing in
                            </>
                        ) : (
                            "Enter"
                        )}
                    </button>
                </form>
            </main>
        );
    }

    return (
        <main
            data-testid="admin-dashboard"
            className="min-h-screen bg-[#F7F5F0] text-[#1A1A1A] py-12 md:py-16"
        >
            <div className="max-w-6xl mx-auto px-6 md:px-12">
                <div className="flex items-center justify-between flex-wrap gap-4 mb-12">
                    <div>
                        <Link
                            to="/"
                            data-testid="admin-home-link"
                            className="eyebrow link-underline inline-flex items-center gap-2"
                        >
                            <ArrowLeft size={12} /> Site
                        </Link>
                        <h1 className="font-serif-display text-4xl md:text-5xl mt-4 leading-none">
                            Gallery <span className="italic">manager</span>
                        </h1>
                    </div>
                    <button
                        onClick={logout}
                        data-testid="admin-logout-button"
                        className="btn-outline"
                    >
                        <LogOut size={14} /> Sign out
                    </button>
                </div>

                <div
                    className="border border-[#1A1A1A] p-6 md:p-10 mb-12"
                    data-testid="admin-upload-card"
                >
                    <p className="eyebrow mb-6">Upload</p>
                    <div className="grid gap-4 md:grid-cols-[1fr_auto] items-end">
                        <label className="block">
                            <span className="eyebrow block mb-2">
                                Caption (optional)
                            </span>
                            <input
                                type="text"
                                value={caption}
                                onChange={(e) => setCaption(e.target.value)}
                                data-testid="admin-caption-input"
                                className="input-edit"
                                placeholder="e.g. Silent Sky · American Stage"
                            />
                        </label>
                        <div>
                            <input
                                ref={fileRef}
                                type="file"
                                accept="image/*"
                                onChange={onFile}
                                data-testid="admin-file-input"
                                className="hidden"
                                id="admin-upload-input"
                            />
                            <label
                                htmlFor="admin-upload-input"
                                data-testid="admin-upload-trigger"
                                className={`btn-ink cursor-pointer ${uploading ? "opacity-50 pointer-events-none" : ""}`}
                            >
                                {uploading ? (
                                    <>
                                        <Loader2
                                            size={14}
                                            className="animate-spin"
                                        />
                                        Uploading
                                    </>
                                ) : (
                                    <>
                                        <Upload size={14} />
                                        Choose Image
                                    </>
                                )}
                            </label>
                        </div>
                    </div>
                    <p className="text-xs text-[#4A4A4A] mt-4">
                        Max 15 MB · JPG, PNG, WebP, GIF · Photos display at
                        their natural aspect ratio, no cropping.
                    </p>
                </div>

                <div className="flex items-baseline justify-between mb-4 flex-wrap gap-3">
                    <p className="eyebrow">
                        Gallery ({images.length})
                    </p>
                    {images.length > 1 && (
                        <p className="text-xs text-[#4A4A4A]">
                            Use ↑ / ↓ to reorder — top-left photo shows first on
                            the site.
                        </p>
                    )}
                </div>

                {images.length === 0 ? (
                    <p
                        data-testid="admin-empty-gallery"
                        className="text-sm text-[#4A4A4A] py-12"
                    >
                        No photos yet. Upload your first to begin.
                    </p>
                ) : (
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                        {images.map((img, idx) => (
                            <div
                                key={img.id}
                                data-testid={`admin-image-${img.id}`}
                                className="group relative bg-[#EBE9E4] border border-[#D1CFC9] flex flex-col"
                            >
                                <div className="relative w-full aspect-[4/5] overflow-hidden">
                                    <img
                                        src={`${API}/files/${img.id}`}
                                        alt={
                                            img.caption ||
                                            img.original_filename
                                        }
                                        className="w-full h-full object-cover"
                                        loading="lazy"
                                    />
                                    <div className="absolute top-2 right-2 flex flex-col gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                                        <button
                                            onClick={() => remove(img.id)}
                                            data-testid={`admin-delete-${img.id}`}
                                            className="w-9 h-9 bg-[#1A1A1A] text-[#F7F5F0] flex items-center justify-center"
                                            aria-label="Delete image"
                                        >
                                            <Trash2 size={14} />
                                        </button>
                                    </div>
                                    <div className="absolute top-2 left-2 text-[10px] tracking-widest uppercase bg-[#1A1A1A] text-[#F7F5F0] px-2 py-1">
                                        #{idx + 1}
                                    </div>
                                </div>
                                <div className="flex items-stretch border-t border-[#D1CFC9]">
                                    <button
                                        onClick={() => move(idx, -1)}
                                        disabled={idx === 0 || reordering}
                                        data-testid={`admin-move-up-${img.id}`}
                                        className="flex-1 py-2 border-r border-[#D1CFC9] hover:bg-[#1A1A1A] hover:text-[#F7F5F0] disabled:opacity-30 disabled:pointer-events-none flex items-center justify-center transition-colors"
                                        aria-label="Move up"
                                    >
                                        <ArrowUp size={14} />
                                    </button>
                                    <button
                                        onClick={() => move(idx, 1)}
                                        disabled={
                                            idx === images.length - 1 ||
                                            reordering
                                        }
                                        data-testid={`admin-move-down-${img.id}`}
                                        className="flex-1 py-2 hover:bg-[#1A1A1A] hover:text-[#F7F5F0] disabled:opacity-30 disabled:pointer-events-none flex items-center justify-center transition-colors"
                                        aria-label="Move down"
                                    >
                                        <ArrowDown size={14} />
                                    </button>
                                </div>
                                {img.caption && (
                                    <p className="px-2 py-2 text-[10px] tracking-wider uppercase text-[#1A1A1A] bg-[#EBE9E4] border-t border-[#D1CFC9]">
                                        {img.caption}
                                    </p>
                                )}
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </main>
    );
}
